import { Upload } from 'lucide-react';
import type { ReactNode } from 'react';
import type { JobDetail, PublicFormField } from '@nest-recruit/shared';
import { Modal } from '../../../components/Modal';
import { SkeletonLines } from '../../../components/Skeleton';
import { useFormDefinition } from '../api/jobs-api';
import styles from './FormPreview.module.css';

/**
 * Workflow review C7 (ADR 0076): the application form's preview, large and in the middle of the screen — the
 * reviewer asked for it there, so it is a centred dialog rather than a side panel (CLAUDE.md §5 allows one when
 * asked). It draws the real form definition the server builds, the one the apply form renders, as the
 * candidate will see it: each question with the shape of its answer. Nothing in it can be typed into.
 */
export function FormPreview({ job, onClose }: { readonly job: JobDetail; readonly onClose: () => void }): ReactNode {
  const definition = useFormDefinition(job.id, job.version);
  const fields = definition.data?.fields ?? [];
  return (
    <Modal isOpen size="large" onClose={onClose} title="What candidates fill in" description={job.publicTitle ?? job.title}>
      {definition.isPending ? (
        <SkeletonLines count={8} />
      ) : (
        <ol className={styles.form} aria-label="The application form">
          {fields.map((field) => (
            <li key={field.key} className={styles.field} data-wide={isWide(field) || undefined}>
              <span className={styles.label}>
                {field.label}
                {!field.isRequired && <span className={styles.optional}>Optional</span>}
              </span>
              <AnswerShape field={field} />
              {field.hint && <span className={styles.hint}>{field.hint}</span>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

/** Choices and long answers take the full row; short answers sit two to a row on a wide screen. */
function isWide(field: PublicFormField): boolean {
  return field.type === 'long_text' || field.type === 'file' || field.options.length > 0 || field.type === 'boolean';
}

const SHORT_PLACEHOLDERS: Readonly<Partial<Record<PublicFormField['type'], string>>> = {
  text: 'Short answer',
  numeric: 'A number',
  date: 'DD-MM-YYYY',
};

function AnswerShape({ field }: { readonly field: PublicFormField }): ReactNode {
  if (field.type === 'file') {
    return (
      <span className={styles.file} aria-hidden>
        <Upload size={16} />
        Upload a file
      </span>
    );
  }
  if (field.type === 'boolean') {
    return (
      <span className={styles.choices} aria-hidden>
        <span className={styles.choice}>Yes</span>
        <span className={styles.choice}>No</span>
      </span>
    );
  }
  if (field.options.length > 0) {
    return (
      <span className={styles.choices} data-several={field.type === 'multi_select' || undefined} aria-hidden>
        {field.options.map((option) => (
          <span key={option.value} className={styles.choice}>
            {option.label}
          </span>
        ))}
      </span>
    );
  }
  if (field.type === 'long_text') {
    return <span className={`${styles.box} ${styles.longBox}`} aria-hidden>Longer answer</span>;
  }
  return (
    <span className={styles.box} aria-hidden>
      {SHORT_PLACEHOLDERS[field.type] ?? 'Answer'}
    </span>
  );
}
